import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import {
  ProposalChangeAddedValue,
  ProposalChangeRemovedValue,
} from "work/proposals/negotiation/proposal-change-value-redline";
import type {
  ProposalFeeScheduleAspectChange,
  ProposalFeeScheduleCategoryChange,
  ProposalFeeScheduleChangeSummary,
} from "work/proposals/negotiation/proposal-fee-schedule-change-summary";

/**
 * The one way a fee-schedule edit is drawn: a row per category, and within a row the WHOLE category,
 * with the columns the actor touched marked as the shared struck-through/blue redline and the rest drawn
 * plainly for context.
 *
 * Showing only the touched columns was accurate and unreadable (#4845): "Rate: $100 → $125" says nothing
 * about which category was repriced without going and looking, and an added or removed category showed
 * one arbitrary column rather than the category that arrived or left. So a category that was added reads
 * entirely as an insertion, one that was removed entirely as a deletion (struck through, columns and
 * all), and one that was edited reads as itself with the edits marked inside it.
 *
 * The change history and the save and submit previews all render through here, so a schedule
 * reads identically wherever the user meets it — the same rule `proposal-change-value-redline.tsx` owns
 * for a single value, applied to the collection that value belongs to.
 */

const CategoryList = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(0.5),
}));

const CategoryRow = styled("div")(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));

const CategoryName = styled("span")(() => ({
  fontWeight: 600,
}));

const AspectSeparator = styled("span")(({ theme }) => ({
  color: theme.palette.text.disabled,
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
}));

const AspectLabel = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

/** An untouched column is context, not news: it reads in the body tone rather than as a redline. */
const AspectUnchangedValue = styled("span")(({ theme }) => ({
  color: theme.palette.text.primary,
}));

export type ProposalFeeScheduleChangeRedlineProps = {
  summary: ProposalFeeScheduleChangeSummary;
};

export default function ProposalFeeScheduleChangeRedline({
  summary,
}: Readonly<ProposalFeeScheduleChangeRedlineProps>) {
  if (summary.categories.length === 0) return null;

  return (
    <CategoryList aria-label="Fee schedule changes">
      {summary.categories.map((category) => (
        <CategoryRow key={category.itemId}>
          <Typography component="span" variant="body2">
            <FeeScheduleCategoryName category={category} />
            {category.aspects.map((aspect, index) => (
              <span key={aspect.label || aspect.aspect || "category"}>
                <AspectSeparator>{index === 0 ? " — " : " | "}</AspectSeparator>
                {/* A statement about the whole category rather than about one of its columns — a
                    category a revert leaves out of the submission — has no column to be labelled by,
                    and an empty label before it would read as a missing word. */}
                {aspect.label ? <AspectLabel>{`${aspect.label}: `}</AspectLabel> : null}
                <FeeScheduleAspectValue aspect={aspect} />
              </span>
            ))}
          </Typography>
        </CategoryRow>
      ))}
    </CategoryList>
  );
}

/**
 * A category names itself, and says at a glance whether it arrived, left, or was edited in place: an
 * added category is drawn as an insertion and a removed one struck through, so the three cases are
 * distinguishable without reading the columns underneath.
 */
function FeeScheduleCategoryName({ category }: Readonly<{ category: ProposalFeeScheduleCategoryChange }>) {
  const name = category.itemLabel ?? "Category";
  if (category.action === "added") {
    return (
      <CategoryName>
        <ProposalChangeAddedValue>{`+ ${name}`}</ProposalChangeAddedValue>
      </CategoryName>
    );
  }
  if (category.action === "removed") {
    return (
      <CategoryName>
        <ProposalChangeRemovedValue>{name}</ProposalChangeRemovedValue>
      </CategoryName>
    );
  }
  return <CategoryName>{name}</CategoryName>;
}

/**
 * One column's value. A changed column is the redline the rest of the app draws for a changed value; an
 * unchanged one is plain text, which is what makes the difference between "this moved" and "this is what
 * the category says" visible at a glance rather than only on reading the numbers.
 */
function FeeScheduleAspectValue({ aspect }: Readonly<{ aspect: ProposalFeeScheduleAspectChange }>) {
  if (!aspect.changed) return <AspectUnchangedValue>{aspect.nextText}</AspectUnchangedValue>;

  return (
    <>
      {aspect.previousText !== undefined && (
        <ProposalChangeRemovedValue>{aspect.previousText}</ProposalChangeRemovedValue>
      )}
      {aspect.nextText !== undefined && <ProposalChangeAddedValue>{aspect.nextText}</ProposalChangeAddedValue>}
    </>
  );
}
